/** Only the message-signing capability is needed to establish the existing GT session. */
export interface MemberMessageSigner {
  address: string;
  signMessage(args: { message: string }): Promise<string>;
}

type Session = { token: string; walletAddress: string; exp: number };
type GasAuthResponse = {
  token?: unknown;
  expiresAt?: unknown;
  user?: { walletAddress?: unknown };
};
let sessions = new WeakMap<MemberMessageSigner, Promise<Session>>();
export function clearMemberWalletSessions() { sessions = new WeakMap(); }
const ADDRESS = /^0x[0-9a-fA-F]{40}$/;

function apiBase(): string {
  return (import.meta.env.VITE_GASTOWN_API_URL || 'https://api.gastown.watchersociety.com').replace(/\/$/, '');
}

async function establishSession(account: MemberMessageSigner): Promise<Session> {
  const walletAddress = account.address;
  if (!ADDRESS.test(walletAddress)) throw new Error('WALLET_CONTINUITY_MISMATCH');
  const message = `GasTown Auth\nWallet: ${walletAddress}\nNonce: ${crypto.randomUUID()}\nIssued At: ${new Date().toISOString()}`;
  const signature = await account.signMessage({ message });
  const response = await fetch(`${apiBase()}/auth/verify`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress, message, signature }),
    signal: AbortSignal.timeout(15_000),
  });
  if (!response.ok) throw new Error('MEMBER_SESSION_UNAVAILABLE');
  const body = await response.json() as GasAuthResponse;
  const session: Session = {
    token: typeof body.token === 'string' ? body.token : '',
    walletAddress: typeof body.user?.walletAddress === 'string' ? body.user.walletAddress : '',
    exp: typeof body.expiresAt === 'number' ? body.expiresAt : Number.NaN,
  };
  if (typeof session.token !== 'string' || !session.token ||
      !ADDRESS.test(session.walletAddress) || session.walletAddress.toLowerCase() !== walletAddress.toLowerCase() ||
      account.address.toLowerCase() !== walletAddress.toLowerCase() ||
      !Number.isFinite(session.exp) || session.exp <= Date.now()) {
    throw new Error('WALLET_CONTINUITY_MISMATCH');
  }
  return session;
}

/** Memory-only, account-bound session. Never store a provider API key in browser storage. */
async function memberSession(account: MemberMessageSigner): Promise<Session> {
  const cached = sessions.get(account);
  if (cached) {
    const session = await cached;
    if (session.exp > Date.now() + 60_000 && session.walletAddress.toLowerCase() === account.address.toLowerCase()) return session;
    sessions.delete(account);
  }
  const pending = establishSession(account);
  sessions.set(account, pending);
  try { return await pending; } catch (error) { sessions.delete(account); throw error; }
}

export async function getMemberAuthToken(account: MemberMessageSigner): Promise<string> {
  return (await memberSession(account)).token;
}

export async function memberWalletRpc(account: MemberMessageSigner, chainId: number, method: string, params: unknown[]): Promise<unknown> {
  const session = await memberSession(account);
  const response = await fetch(`${apiBase()}/wallet/rpc`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
    body: JSON.stringify({ chainId, request: { jsonrpc: '2.0', id: 1, method, params } }),
    signal: AbortSignal.timeout(15_000),
  });
  if (!response.ok) {
    if (response.status === 401) sessions.delete(account);
    throw new Error('CHAIN_DATA_UNAVAILABLE');
  }
  const body = await response.json();
  if (body?.jsonrpc !== '2.0' || body.id !== 1 || body.error || !('result' in body)) throw new Error('CHAIN_DATA_UNAVAILABLE');
  return body.result;
}
